'use client';

import { useMutation } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { useAuth } from 'react-oidc-context';

import { updateAnswer, updateComment, updateQuestion, updateReview } from '../../../apis/feedbacks';
import type { FeedbackShare, FeedbackType } from '../../../types/feedbacks';

type FeedbackUpdateMutation = {
  onSuccess?: (data: AxiosResponse, variables: NewComment) => void;
};

type NewCommentOptions = {
  feedbackId: string;
  content: string;
  ranges: FeedbackShare['ranges'];
  mentions: FeedbackShare['mentions'];
};

type BaseNewComment = {
  feedbackType: Exclude<FeedbackType, 'Review'>;
} & NewCommentOptions;

type NewReviewComment = {
  feedbackType: Extract<FeedbackType, 'Review'>;
  score: number;
} & NewCommentOptions;

type NewComment = BaseNewComment | NewReviewComment;

export const useFeedbackUpdateMutation = (args: FeedbackUpdateMutation) => {
  const { onSuccess } = args;
  const { user } = useAuth();

  return useMutation(
    (newComment: NewComment) => {
      switch (newComment.feedbackType) {
        case 'Comment': {
          return updateComment(
            newComment.feedbackId,
            newComment.content,
            newComment.ranges,
            newComment.mentions,
            user?.access_token as string,
          );
        }
        case 'Question': {
          return updateQuestion(
            newComment.feedbackId,
            newComment.content,
            newComment.ranges,
            newComment.mentions,
            user?.access_token as string,
          );
        }
        case 'Answer': {
          return updateAnswer(
            newComment.feedbackId,
            newComment.content,
            newComment.ranges,
            newComment.mentions,
            user?.access_token as string,
          );
        }
        case 'CommentChild': {
          return updateComment(
            newComment.feedbackId,
            newComment.content,
            newComment.ranges,
            newComment.mentions,
            user?.access_token as string,
          );
        }
        case 'Review': {
          return updateReview(
            newComment.feedbackId,
            newComment.content,
            newComment.ranges,
            newComment.mentions,
            newComment.score,
            user?.access_token as string,
          );
        }
        default: {
          throw new Error('Not a feedback type');
        }
      }
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
    },
  );
};
